/**
 * <kyn-carousel> — the one endless, slowly moving row used across the theme
 * (home capsule row in sections/capsule-grid.liquid, "Pasuje do" in sections/product-recommendations.liquid).
 *
 * Markup:
 *   <kyn-carousel class="kyn-carousel" data-seconds="6">        (or data-speed="18" in px/s)
 *     <div class="kyn-carousel__rail">
 *       <div class="kyn-carousel__set"> …items… </div>
 *     </div>
 *   </kyn-carousel>
 * Item width and gap come from --kyn-carousel-item and --kyn-carousel-gap (assets/kyn.css).
 *
 * The set is cloned once (hidden from screen readers, out of the tab order) so the row loops without
 * a seam: the position wraps by one set width. The rail moves with a transform, never with scrollLeft:
 * iOS Safari tears the picture (cards drawn over each other) when a scroller's scrollLeft is written
 * every frame while the page scrolls.
 * It drifts left on its own. Mouse over it stops it; it moves both ways by mouse drag, swipe with a
 * short fling (vertical swipes stay with the page: touch-action: pan-y) and sideways trackpad scroll,
 * and drifts again 2.5 s after a touch. A drag does not open the card under it. With
 * prefers-reduced-motion it never drifts but can still be moved. When one set is no wider than the
 * row, nothing moves.
 */
class KynCarousel extends HTMLElement {
  /** Rail offset in px, kept in [0, loop). */
  #pos = 0;
  /** Width of one set, i.e. the loop length. */
  #loop = 0;
  /** Drift speed, px/s. */
  #speed = 0;
  /** Release speed of a drag (px/ms), decays to zero. */
  #glide = 0;
  #frame = 0;
  #last = 0;
  #hovered = false;
  #holdUntil = 0;
  #visible = false;
  /** @type {{ id: number, x: number, pos: number, moved: boolean, lastX: number, lastT: number, v: number } | null} */
  #drag = null;
  #suppressClick = false;
  /** @type {HTMLElement | null} */
  #rail = null;
  /** @type {HTMLElement | null} */
  #set = null;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {ResizeObserver | undefined} */
  #resizer;

  connectedCallback() {
    this.#rail = this.querySelector('.kyn-carousel__rail');
    this.#set = this.querySelector('.kyn-carousel__set');
    if (!this.#rail || !this.#set) return;
    this.#cloneSet();
    this.#measure();

    this.addEventListener('pointerenter', this.#onEnter);
    this.addEventListener('pointerleave', this.#onLeave);
    this.addEventListener('pointerdown', this.#onDown);
    this.addEventListener('pointermove', this.#onMove);
    this.addEventListener('pointerup', this.#onUp);
    this.addEventListener('pointercancel', this.#onCancel);
    this.addEventListener('wheel', this.#onWheel, { passive: false });
    this.addEventListener('scroll', this.#onScroll, { passive: true });
    this.addEventListener('focusin', this.#onFocus);
    this.addEventListener('click', this.#onClick, true);
    this.addEventListener('dragstart', this.#onDragStart);

    this.#resizer = new ResizeObserver(() => this.#measure());
    this.#resizer.observe(this);
    this.#resizer.observe(this.#set);
    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = Boolean(entry?.isIntersecting);
      if (this.#visible) this.#run();
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#frame);
    this.#observer?.disconnect();
    this.#resizer?.disconnect();
    this.removeEventListener('pointerenter', this.#onEnter);
    this.removeEventListener('pointerleave', this.#onLeave);
    this.removeEventListener('pointerdown', this.#onDown);
    this.removeEventListener('pointermove', this.#onMove);
    this.removeEventListener('pointerup', this.#onUp);
    this.removeEventListener('pointercancel', this.#onCancel);
    this.removeEventListener('wheel', this.#onWheel);
    this.removeEventListener('scroll', this.#onScroll);
    this.removeEventListener('focusin', this.#onFocus);
    this.removeEventListener('click', this.#onClick, true);
    this.removeEventListener('dragstart', this.#onDragStart);
  }

  /** The second copy of the set: looks the same, stays clickable, but is invisible to assistive tech. */
  #cloneSet() {
    if (!this.#rail || !this.#set || this.#rail.querySelector('[data-kyn-carousel-copy]')) return;
    const copy = /** @type {HTMLElement} */ (this.#set.cloneNode(true));
    copy.dataset.kynCarouselCopy = '';
    copy.setAttribute('aria-hidden', 'true');
    for (const el of copy.querySelectorAll('a, button, input, select, textarea, [tabindex]')) el.setAttribute('tabindex', '-1');
    for (const el of copy.querySelectorAll('[id]')) el.removeAttribute('id');
    this.#rail.append(copy);
  }

  #measure() {
    if (!this.#set) return;
    this.#loop = this.#set.offsetWidth;
    const speed = Number(this.dataset.speed);
    const count = this.#set.children.length || 1;
    const seconds = Number(this.dataset.seconds) || 6;
    this.#speed = speed > 0 ? speed : this.#loop / count / seconds;
    this.#apply();
  }

  #moves() {
    return this.#loop > this.clientWidth;
  }

  /** Wraps the position into one set width (both sets look the same) and moves the rail. */
  #apply() {
    this.#pos = this.#moves() ? ((this.#pos % this.#loop) + this.#loop) % this.#loop : 0;
    if (this.#rail) this.#rail.style.transform = `translate3d(${-this.#pos}px, 0, 0)`;
  }

  #paused() {
    return this.#hovered || this.#drag !== null || performance.now() < this.#holdUntil;
  }

  #run() {
    cancelAnimationFrame(this.#frame);
    this.#last = performance.now();
    const step = (/** @type {number} */ now) => {
      if (!this.#visible) return;
      const dt = Math.min(now - this.#last, 100);
      this.#last = now;
      if (this.#glide && !this.#drag) {
        this.#pos += this.#glide * dt;
        this.#glide *= Math.exp(-dt / 325);
        if (Math.abs(this.#glide) < 0.02) this.#glide = 0;
        this.#apply();
      } else if (this.#moves() && !this.#paused() && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.#pos += (this.#speed * dt) / 1000;
        this.#apply();
      }
      this.#frame = requestAnimationFrame(step);
    };
    this.#frame = requestAnimationFrame(step);
  }

  /** Pauses the drift for a moment after touch, wheel or keyboard focus. */
  #hold() {
    this.#holdUntil = performance.now() + 2500;
  }

  #onEnter = (/** @type {PointerEvent} */ event) => {
    if (event.pointerType === 'mouse') this.#hovered = true;
  };

  #onLeave = (/** @type {PointerEvent} */ event) => {
    if (event.pointerType === 'mouse') this.#hovered = false;
  };

  #onDown = (/** @type {PointerEvent} */ event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.#suppressClick = false;
    this.#glide = 0;
    const now = performance.now();
    this.#drag = { id: event.pointerId, x: event.clientX, pos: this.#pos, moved: false, lastX: event.clientX, lastT: now, v: 0 };
  };

  /** Horizontal moves only: vertical swipes go to the page and end in pointercancel. */
  #onMove = (/** @type {PointerEvent} */ event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.id || !this.#moves()) return;
    const dx = event.clientX - drag.x;
    if (!drag.moved) {
      if (Math.abs(dx) < 6) return;
      drag.moved = true;
      this.classList.add('is-dragging');
      this.setPointerCapture?.(event.pointerId);
    }
    const now = performance.now();
    const dt = now - drag.lastT;
    if (dt > 0) drag.v = 0.8 * ((drag.lastX - event.clientX) / dt) + 0.2 * drag.v;
    drag.lastX = event.clientX;
    drag.lastT = now;
    this.#pos = drag.pos - dx;
    this.#apply();
  };

  #onUp = (/** @type {PointerEvent} */ event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.id) return;
    this.#suppressClick = drag.moved;
    // A pause before release means no fling.
    if (drag.moved && performance.now() - drag.lastT < 80) this.#glide = Math.max(-4, Math.min(4, drag.v));
    this.#end(event);
  };

  #onCancel = (/** @type {PointerEvent} */ event) => {
    if (this.#drag && event.pointerId === this.#drag.id) this.#end(event);
  };

  /** @param {PointerEvent} event */
  #end(event) {
    if (this.hasPointerCapture?.(event.pointerId)) this.releasePointerCapture(event.pointerId);
    this.#drag = null;
    this.classList.remove('is-dragging');
    if (event.pointerType !== 'mouse') this.#hold();
  }

  /** Sideways trackpad scroll moves the row; vertical wheel stays with the page. */
  #onWheel = (/** @type {WheelEvent} */ event) => {
    if (!this.#moves() || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
    event.preventDefault();
    this.#glide = 0;
    this.#pos += event.deltaX;
    this.#apply();
    this.#hold();
  };

  /** Keyboard focus on a card out of view scrolls the clipped row: move that scroll into the rail. */
  #onScroll = () => {
    if (!this.scrollLeft) return;
    this.#pos += this.scrollLeft;
    this.scrollLeft = 0;
    this.#apply();
  };

  #onFocus = (/** @type {FocusEvent} */ event) => {
    if (event.target instanceof Element && event.target.matches(':focus-visible')) this.#hold();
  };

  /** A drag ends with a click on a card; that click must not open the product. */
  #onClick = (/** @type {MouseEvent} */ event) => {
    if (!this.#suppressClick) return;
    this.#suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  #onDragStart = (/** @type {DragEvent} */ event) => {
    event.preventDefault();
  };
}

if (!customElements.get('kyn-carousel')) customElements.define('kyn-carousel', KynCarousel);
